import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, ArrowRight, TrendingUp, Coins, PiggyBank, Wallet, BarChart3, CircleDollarSign, Landmark, BadgePercent, Volume2, VolumeX } from 'lucide-react';
import { useApp } from '@/store/appContext';
import KompaLogo from '@/modules/shared/KompaLogo';
import StepIndicator from '@/modules/shared/StepIndicator';
import { WELCOME_VIDEO_URL } from '@/lib/constants';

const FLOATING_ICONS = [
  { icon: TrendingUp, x: '12%', y: '5%', size: 28, delay: 0, dur: 5, color: '#fbb040' },
  { icon: Coins, x: '75%', y: '0%', size: 24, delay: 0.4, dur: 6, color: '#1f2d5a' },
  { icon: PiggyBank, x: '45%', y: '-5%', size: 32, delay: 0.8, dur: 5.5, color: '#fbb040' },
  { icon: Wallet, x: '88%', y: '15%', size: 22, delay: 1.2, dur: 6.5, color: '#e8a020' },
  { icon: BarChart3, x: '5%', y: '25%', size: 26, delay: 0.6, dur: 5.8, color: '#1f2d5a' },
  { icon: CircleDollarSign, x: '60%', y: '20%', size: 20, delay: 1.5, dur: 7, color: '#fbb040' },
  { icon: Landmark, x: '30%', y: '30%', size: 18, delay: 1.8, dur: 6.2, color: '#c48510' },
  { icon: BadgePercent, x: '82%', y: '35%', size: 22, delay: 0.3, dur: 5.3, color: '#1f2d5a' },
];

function FloatingIcon3D({ icon: Icon, x, y, size, delay, dur, color }) {
  return (
    <motion.div
      className="absolute"
      style={{ left: x, top: y, perspective: 400 }}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{
        opacity: [0, 0.7, 0.5, 0.8, 0.6, 0.7],
        y: [0, -14, -6, -18, -8, 0],
        rotateY: [0, 180, 360],
        scale: [0.85, 1.05, 0.9, 1.1, 0.95, 0.85],
      }}
      transition={{
        delay,
        duration: dur,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    >
      <Icon style={{ width: size, height: size, color }} />
    </motion.div>
  );
}

function AnimatedTitle({ text, className, testId }) {
  const letters = text.split('');
  return (
    <h1 className={className} data-testid={testId}>
      {letters.map((char, i) => (
        <motion.span
          key={i}
          className="inline-block"
          initial={{ opacity: 0, scale: 0.5, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{
            delay: 0.4 + i * 0.04,
            type: 'spring',
            stiffness: 300,
            damping: 15,
          }}
          whileInView={{
            scale: [1, 1.15, 1],
            transition: {
              delay: 2 + Math.random() * 3,
              duration: 0.4,
              repeat: Infinity,
              repeatDelay: 3 + Math.random() * 4,
            },
          }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </h1>
  );
}

export default function WelcomePage() {
  const { user, setCurrentStep } = useApp();
  const videoRef = useRef(null);
  // Browsers block autoplay-with-sound by default. We start muted so the
  // video can autoplay; the user can toggle audio via the floating button.
  // When the user un-mutes, we also re-play in case the browser paused.
  const [muted, setMuted] = useState(true);
  // If the video URL fails to load (CORS, asset removed, network drop),
  // we flip to the fallback flow so the user is never stuck.
  const [videoFailed, setVideoFailed] = useState(false);

  // Auto-advance after the welcome video finishes.
  // If no video URL is configured we fall back to the existing
  // "Siguiente" button so the flow never gets stuck.
  const handleVideoEnded = () => setCurrentStep(2);
  const handleVideoError = () => setVideoFailed(true);

  const toggleAudio = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    setMuted(next);
    v.muted = next;
    // If the user un-muted via gesture, the browser will allow audio.
    // Resume playback in case it was paused for any reason.
    if (!next) {
      try { v.play().catch(() => {}); } catch { /* noop */ }
    }
  };

  const hasVideo = Boolean(WELCOME_VIDEO_URL) && !videoFailed;

  return (
    <div className="min-h-screen flex flex-col bg-white px-6 md:px-8 pt-10 md:pt-14 pb-8 md:pb-10 max-w-2xl mx-auto w-full">
      <div className="flex items-center justify-between mb-2">
        <KompaLogo size="sm" />
        <span className="text-sm font-medium text-gray-500">
          Hola, {user.nombre || user.email?.split('@')[0] || 'Kompa'}
        </span>
      </div>

      <StepIndicator current={0} />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        className="flex-1 flex flex-col items-center justify-center text-center"
      >
        {/* Floating 3D financial icons */}
        <div className="relative w-full h-32 mb-4">
          {FLOATING_ICONS.map((item, i) => (
            <FloatingIcon3D key={i} {...item} />
          ))}
        </div>

        <AnimatedTitle
          text="TU PRIMERA META"
          className="text-4xl font-bold text-black tracking-tight mb-3"
          testId="welcome-title"
        />
        <p className="text-gray-500 font-light text-base max-w-xs leading-relaxed mb-10">
          Vamos a crear tu primer plan de capitalización juntos. Es rápido, fácil y totalmente personalizable.
        </p>

        {/* Welcome video — autoplay + no controls. Falls back to the
            decorative placeholder when no URL is configured. */}
        {hasVideo ? (
          <motion.div
            className="relative w-full aspect-video rounded-2xl overflow-hidden mb-10 bg-black shadow-lg"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            data-testid="welcome-video-container"
          >
            <video
              ref={videoRef}
              src={WELCOME_VIDEO_URL}
              autoPlay
              muted={muted}
              playsInline
              controls={false}
              disablePictureInPicture
              controlsList="nodownload nofullscreen noremoteplayback"
              onContextMenu={(e) => e.preventDefault()}
              onEnded={handleVideoEnded}
              onError={handleVideoError}
              className="w-full h-full object-cover pointer-events-none select-none"
              data-testid="welcome-video"
            />
            {/* Floating audio toggle — required because the autoplay flow
                must start muted by browser policy. A single tap of this
                button (a user gesture) is what unlocks audio playback. */}
            <motion.button
              type="button"
              onClick={toggleAudio}
              whileTap={{ scale: 0.92 }}
              whileHover={{ scale: 1.05 }}
              className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-black/55 backdrop-blur-md text-white flex items-center justify-center shadow-lg ring-1 ring-white/15 hover:bg-black/70 transition-colors z-10"
              aria-label={muted ? 'Activar audio' : 'Silenciar audio'}
              data-testid="welcome-video-audio-toggle"
            >
              {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </motion.button>
            {muted && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                className="absolute bottom-3 left-3 bg-black/55 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ring-1 ring-white/15 z-10 select-none"
                data-testid="welcome-video-mute-hint"
              >
                Toca para escuchar
              </motion.div>
            )}
          </motion.div>
        ) : (
          <motion.div
            className="w-full aspect-video rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-100 flex items-center justify-center relative overflow-hidden mb-10 cursor-pointer group"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            data-testid="video-placeholder"
          >
            <div className="absolute inset-0 bg-[#1f2d5a]/5" />
            <div className="relative flex flex-col items-center gap-3">
              <motion.div
                className="w-14 h-14 bg-[#fbb040] rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow"
                whileHover={{ scale: 1.1 }}
              >
                <Play className="w-6 h-6 text-black ml-1" fill="black" />
              </motion.div>
              <span className="text-sm font-medium text-[#1f2d5a]">
                Conoce a Kompaisito
              </span>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Manual "Siguiente" CTA — only shown when no video is configured.
          When the video plays, `onEnded` advances the flow automatically. */}
      {!hasVideo && (
        <motion.button
          onClick={() => setCurrentStep(2)}
          whileTap={{ scale: 0.97 }}
          className="w-full bg-[#fbb040] text-black font-bold rounded-full h-14 flex items-center justify-center gap-2 hover:bg-[#ffa000] transition-colors text-lg"
          data-testid="welcome-next-button"
        >
          Siguiente
          <ArrowRight className="w-5 h-5" />
        </motion.button>
      )}
    </div>
  );
}
